import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X, Send, FileText, Loader2 } from 'lucide-react';
import { moduleApi } from '../../lib/api';

interface DocumentTemplate { id: string; name: string; is_active: boolean; }

const inputCls = "w-full bg-surface-soft border border-border rounded-xl px-3 py-2.5 text-sm text-content outline-none focus:border-brand-500 transition-colors";
const labelCls = "text-xs font-bold text-content-muted uppercase tracking-widest mb-1.5 block";

export function SendDocumentByEmailModal({
    onClose,
    onSent,
}: {
    onClose: () => void;
    onSent: () => void;
}) {
    const [templates, setTemplates] = React.useState<DocumentTemplate[]>([]);
    const [loading, setLoading] = React.useState(true);
    const [selectedId, setSelectedId] = React.useState('');
    const [email, setEmail] = React.useState('');
    const [sending, setSending] = React.useState(false);
    const [error, setError] = React.useState('');
    const [sent, setSent] = React.useState(false);

    React.useEffect(() => {
        moduleApi.getDocumentTemplates()
            .then((d: any) => setTemplates((Array.isArray(d) ? d : []).filter((t: any) => t.is_active)))
            .catch(() => setTemplates([]))
            .finally(() => setLoading(false));
    }, []);

    const handleSend = async () => {
        if (!selectedId || !email.trim()) return;
        setSending(true);
        setError('');
        try {
            const res: any = await moduleApi.sendDocumentTemplate(selectedId, { email: email.trim() });
            if (res?.error) { setError(res.error); return; }
            setSent(true);
            onSent();
        } catch (e: any) {
            setError(e?.message || 'Erreur lors de l\'envoi.');
        } finally {
            setSending(false);
        }
    };

    return (
        <AnimatePresence>
            <div className="fixed inset-0 z-[70] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
                <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.96 }}
                    className="bg-surface border border-border rounded-2xl w-full max-w-sm shadow-2xl overflow-hidden">
                    <div className="flex items-center justify-between p-5 border-b border-border">
                        <h2 className="text-base font-bold text-content flex items-center gap-2"><FileText size={17} className="text-brand-500" /> Envoyer une documentation</h2>
                        <button onClick={onClose} className="p-1.5 text-content-muted hover:text-content rounded-lg hover:bg-surface-soft transition-all"><X size={18} /></button>
                    </div>

                    {sent ? (
                        <div className="p-6 space-y-3 text-center">
                            <p className="text-sm text-content">Démarche envoyée.</p>
                            <button onClick={onClose} className="px-5 py-2.5 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg font-bold text-sm transition-all">Fermer</button>
                        </div>
                    ) : loading ? (
                        <div className="flex justify-center py-8"><Loader2 size={20} className="animate-spin text-content-muted" /></div>
                    ) : templates.length === 0 ? (
                        <div className="p-6 text-center space-y-2">
                            <p className="text-sm text-content-muted">Aucun modèle actif. Créez-en un dans Paramètres &gt; Démarches client.</p>
                            <button onClick={onClose} className="px-5 py-2.5 rounded-xl border border-border text-content-muted hover:text-content font-bold text-sm transition-all">Fermer</button>
                        </div>
                    ) : (
                        <>
                            <div className="p-5 space-y-4">
                                <div>
                                    <label className={labelCls}>Adresse email du destinataire</label>
                                    <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} placeholder="client@email.com" />
                                    <p className="text-[11px] text-content-muted mt-1">Si cette adresse correspond déjà à un contact CRM, la démarche lui sera directement rattachée. Sinon, le destinataire devra renseigner ses informations (nom, société, téléphone) en répondant — un contact CRM sera alors créé automatiquement.</p>
                                </div>
                                <div className="space-y-2">
                                    <label className={labelCls}>Modèle</label>
                                    {templates.map((t) => (
                                        <button key={t.id} onClick={() => setSelectedId(t.id)}
                                            className={`w-full text-left px-4 py-3 rounded-xl border text-sm font-bold transition-all ${selectedId === t.id ? 'bg-brand-500/10 border-brand-500 text-brand-600 dark:text-brand-400' : 'border-border text-content hover:bg-surface-soft'}`}>
                                            {t.name}
                                        </button>
                                    ))}
                                </div>
                                {error && <p className="text-xs text-red-500 font-semibold">{error}</p>}
                            </div>
                            <div className="flex justify-end gap-3 p-5 border-t border-border">
                                <button onClick={onClose} className="px-4 py-2.5 rounded-xl border border-border text-content-muted hover:text-content hover:bg-surface-soft font-bold text-sm transition-all">Annuler</button>
                                <button onClick={handleSend} disabled={!selectedId || !email.trim() || sending} className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg font-bold text-sm transition-all disabled:opacity-50">
                                    <Send size={15} /> {sending ? 'Envoi...' : 'Envoyer'}
                                </button>
                            </div>
                        </>
                    )}
                </motion.div>
            </div>
        </AnimatePresence>
    );
}
